import {Component, OnInit, ViewChild} from '@angular/core';
import {UserService} from '../../services/user/user.service';
import {User, UserDefaults} from '../../models/user';
import {Household} from '../../models/household';
import {HouseholdService} from '../../services/property/household.service';
import {ContextStoreService} from '../../services/context/context-store.service';
import {faEnvelope, faEdit, faBell, faTrash} from '@fortawesome/free-solid-svg-icons';
import {ApiInfo} from '../../models/apiinfo';
import {ApiinfoService} from '../../services/meta/apiinfo.service';
import {PushDeviceService} from '../../services/push/push-device.service';
import {LocalNotificationService} from '../../services/push/local-notification.service';
import {ConsumptionCheckStore} from '../../services/consumption/consumption-check-store.service';
import {PushDevice, NotificationPreference} from '../../models/push-device';
import {UiToastService} from '../../shared/ui';
import {AppEventsService} from "../../services/app-events/app-events.service";
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UiModalComponent} from '../../shared/ui';
import { AppLogger, AppLoggerService } from '../../core/logging';
import {environment} from '../../env';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: false
})
export class ProfileComponent implements OnInit {

  private readonly tag = '[Profile]';

  private readonly log: AppLogger;

  user: User = undefined;
  households: Household[];
  defaults: UserDefaults;

  faEdit = faEdit;
  faEnvelope = faEnvelope;
  faBell = faBell;
  faTrash = faTrash;

  apiInfo: ApiInfo;

  // Push notifications
  devices: PushDevice[];
  devicesLoading = false;
  sendingTest = false;
  sendingReminder = false;
  addingFake = false;
  removingId?: number;

  // Notification-type preferences
  preferences: NotificationPreference[];
  preferencesLoading = false;
  savingPreferenceKey?: string;

  // Fake devices are a local/stage testing affordance only — never production.
  // Both local and stage builds ship production:false, so this hides the control
  // in the production build while keeping it available everywhere else.
  readonly showFakeDevices = !environment.production;

  settingDefaultHouseholdId: number | undefined;

  // Household modal
  @ViewChild('addHouseholdModal', {static: false}) addHouseholdModal?: UiModalComponent;
  householdForm: UntypedFormGroup;
  householdSaving = false;
  householdError?: string;

  constructor(
    public userService: UserService,
    private toastService: UiToastService,
    private apiInfoService: ApiinfoService,
    private contextStore: ContextStoreService,
    private householdService: HouseholdService,
    private fb: UntypedFormBuilder,
    private appLogger: AppLoggerService,
    private appEvents: AppEventsService,
    private pushDeviceService: PushDeviceService,
    private localNotifications: LocalNotificationService,
    private consumptionChecks: ConsumptionCheckStore
  ) {
    this.log = this.appLogger.createForClass(ProfileComponent);

    this.userService.getUserProfile().subscribe((u) => {
      this.user = u;
    });

    this.loadApiInfo();

    // Initialize household form
    this.householdForm = this.fb.group({
      isDefault: [false],
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  ngOnInit(): void {
    this.listenToDefaults();
    this.loadDevices();
    this.loadPreferences();
  }

  loadPreferences(): void {
    this.preferencesLoading = true;
    this.pushDeviceService.getPreferences().subscribe({
      next: (data) => {
        this.preferences = data;
        this.preferencesLoading = false;
      },
      error: (e) => {
        this.log.error('Failed to load notification preferences', e);
        this.preferences = [];
        this.preferencesLoading = false;
      }
    });
  }

  togglePreference(pref: NotificationPreference, enabled: boolean): void {
    if (this.savingPreferenceKey) return;

    const previous = pref.enabled;
    pref.enabled = enabled; // optimistic
    this.savingPreferenceKey = pref.key;

    this.pushDeviceService.updatePreferences([{key: pref.key, enabled}]).subscribe({
      next: (updated) => {
        this.preferences = updated;
        this.savingPreferenceKey = undefined;
      },
      error: (e) => {
        this.log.error('Failed to update notification preference', e, { key: pref.key });
        pref.enabled = previous; // roll back
        this.toastService.error('Failed to update preference', { durationMs: 5000 });
        this.savingPreferenceKey = undefined;
      }
    });
  }

  loadDevices(): void {
    this.devicesLoading = true;
    this.pushDeviceService.listDevices().subscribe({
      next: (data) => {
        this.devices = data;
        this.devicesLoading = false;
      },
      error: (e) => {
        this.log.error('Failed to load push devices', e);
        this.devices = [];
        this.devicesLoading = false;
      }
    });
  }

  /**
   * Fires a consumption reminder right now, so the whole path — notification,
   * click, the screen it opens — can be walked through on demand.
   *
   * Three things happen, deliberately in this order:
   *   1. A preview round is seeded locally, so the screen has something to show
   *      even while the reminder backend does not exist yet.
   *   2. A browser notification is shown, because the web app has no push
   *      channel and this is the only clickable notification it can produce.
   *   3. The real push is sent to any registered device, which is what a phone
   *      would receive in production.
   *
   * A failure in step 3 is reported but does not undo the rest: the point of
   * the button is seeing the flow, and the flow works without a device.
   */
  async sendTestReminder(): Promise<void> {
    if (this.sendingReminder) return;
    this.sendingReminder = true;

    this.consumptionChecks.seedPreviewRound();

    const outcome = await this.localNotifications.show({
      title: 'Quick question',
      body: 'Have you used up the cucumber? Two things to check.',
      route: '/quick-check',
      tag: 'consumption_check',
    });

    if (outcome === 'denied') {
      this.toastService.info(
        'Your browser is blocking notifications — open Quick check from the menu instead.',
        {durationMs: 5000}
      );
    } else if (outcome === 'unsupported') {
      this.toastService.info('This browser cannot show notifications.', {durationMs: 4000});
    }

    this.pushDeviceService.sendTest('consumption_check').subscribe({
      next: (result) => {
        if (result.delivered_to > 0) {
          this.toastService.success(
            `Reminder sent to ${result.delivered_to} device(s).`,
            {durationMs: 3000}
          );
        }
        this.sendingReminder = false;
      },
      error: (e) => {
        // Expected while the API is down or push is unconfigured — the local
        // notification and the preview round already did their job.
        this.log.warn('Failed to send test reminder push', {status: e?.status});
        this.sendingReminder = false;
      }
    });
  }

  sendTestNotification(): void {
    if (this.sendingTest) return;

    this.sendingTest = true;
    this.pushDeviceService.sendTest().subscribe({
      next: (result) => {
        if (result.delivered_to > 0) {
          this.toastService.success(
            `Test notification sent to ${result.delivered_to} device(s).`,
            { durationMs: 3000 }
          );
        } else {
          this.toastService.info(result.message || 'No registered devices to notify.', { durationMs: 4000 });
        }
        this.sendingTest = false;
      },
      error: (e) => {
        this.log.error('Failed to send test notification', e);
        const msg = e?.status === 503
          ? 'Push notifications are not configured on the server.'
          : (e?.error?.message || 'Failed to send test notification');
        this.toastService.error(msg, { durationMs: 5000 });
        this.sendingTest = false;
      }
    });
  }

  addFakeDevice(): void {
    if (this.addingFake) return;

    this.addingFake = true;
    this.pushDeviceService.addFake('sandbox').subscribe({
      next: (device) => {
        this.toastService.success(`Fake device added (…${device.token_suffix}).`, { durationMs: 3000 });
        this.addingFake = false;
        this.loadDevices();
      },
      error: (e) => {
        this.log.error('Failed to add fake device', e);
        const msg = e?.status === 404
          ? 'Fake devices are not available on this environment.'
          : (e?.error?.message || 'Failed to add fake device');
        this.toastService.error(msg, { durationMs: 5000 });
        this.addingFake = false;
      }
    });
  }

  /** Whether a device is a synthetic test device (removable from the UI). */
  isFakeDevice(d: PushDevice): boolean {
    return d.platform === 'ios-fake';
  }

  removeDevice(d: PushDevice): void {
    if (this.removingId) return;

    this.removingId = d.id;
    this.pushDeviceService.remove(d.id).subscribe({
      next: () => {
        this.toastService.success('Device removed.', { durationMs: 3000 });
        this.removingId = undefined;
        this.loadDevices();
      },
      error: (e) => {
        this.log.error('Failed to remove device', e, { deviceId: d.id });
        this.toastService.error(e?.error?.message || 'Failed to remove device', { durationMs: 5000 });
        this.removingId = undefined;
      }
    });
  }

  loadHouseholds(): void {
    this.householdService.households().subscribe((data) => {
      this.households = data;
    });
  }

  listenToDefaults(): void {
    this.contextStore.household$.subscribe(() => {
      this.loadHouseholds();
    });
  }

  openNewHousehold(): void {
    this.householdError = undefined;
    this.householdForm.reset({
      isDefault: false,
      name: ''
    });
    this.householdSaving = false;
    this.addHouseholdModal?.open();
  }

  closeHouseholdModal(): void {
    this.addHouseholdModal?.close();
  }

  saveHousehold(): void {
    if (this.householdSaving || this.householdForm.invalid) return;

    this.householdSaving = true;
    this.householdError = undefined;

    const payload: Household = {
      name: this.householdForm.value.name,
      is_default: this.householdForm.value.isDefault
    };

    this.householdService.add(payload).subscribe({
      next: () => {
        this.toastService.success('Household created', { durationMs: 3000 });
        this.loadHouseholds();
        this.appEvents.notifyHouseholdChanged();
        this.householdSaving = false;
        this.addHouseholdModal?.close();
      },
      error: (e) => {
        this.log.error('Failed to create household', e, { payload });
        this.householdError = e?.error?.message || 'Failed to create household';
        this.toastService.error('Failed to create household', { durationMs: 5000 });
        this.householdSaving = false;
      }
    });
  }

  loadApiInfo(): void {
    this.apiInfoService.getApiInfo().subscribe((data) => {
      this.apiInfo = data;
    });
  }

  setDefaultHousehold(h: Household, event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (!h?.id) return;
    if (h.is_default) return;
    if (this.settingDefaultHouseholdId) return;

    this.settingDefaultHouseholdId = h.id;

    // Minimal payload: backend expects the updatable properties.
    const payload: Household = {
      name: h.name,
      is_default: true
    };

    this.householdService.update(h.id, payload).subscribe({
      next: () => {
        this.toastService.success(`Set "${h.name}" as default`, { durationMs: 3000 });
        this.loadHouseholds();
        // Let other parts (context selector) reload & re-pick default.
        this.appEvents.notifyHouseholdChanged();
        this.settingDefaultHouseholdId = undefined;
      },
      error: (e) => {
        this.log.error('Failed to set default household', e, { householdId: h?.id, name: h?.name });
        this.toastService.error('Failed to set default household', { durationMs: 5000 });
        this.settingDefaultHouseholdId = undefined;
      }
    });
  }
}
